/**
 * Custom Hook for Configurator State Management
 *
 * @package
 */

import { useState, useEffect, useRef, useCallback } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { normaliseQuantity } from './priceHelpers';

// Collapses a burst of clicks without leaving the share buttons disabled for long.
const SAVE_DEBOUNCE_MS = 500;

/**
 * Custom hook for managing configurator state with WordPress transients
 *
 * @param {Object} initialConfig Initial configuration.
 * @return {Object} State and setters.
 */
export const useConfiguratorState = ( initialConfig = {} ) => {
	const [ selectedBaseModel, setSelectedBaseModel ] = useState( initialConfig.baseModel || null );
	const [ selectedProducts, setSelectedProducts ] = useState( initialConfig.products || [] );
	const [ selectedBundle, setSelectedBundle ] = useState( initialConfig.bundle || null );
	// Product ID => quantity, holding only products taken more than once. Kept beside
	// selectedProducts so every consumer of the ID list carries on working unchanged.
	const [ productQuantities, setProductQuantities ] = useState( initialConfig.quantities || {} );
	const [ shareableId, setShareableId ] = useState( null );
	const [ expirationDate, setExpirationDate ] = useState( null );
	const shareableIdRef = useRef( null );
	const isInitialLoad = useRef( true );
	const hasLoadedFromCode = useRef( false );

	useEffect( () => {
		if ( isInitialLoad.current ) {
			return;
		}

		if ( hasLoadedFromCode.current ) {
			hasLoadedFromCode.current = false;
			return;
		}

		if ( ! selectedBaseModel && ( ! selectedProducts || selectedProducts.length === 0 ) && ! selectedBundle ) {
			setShareableId( null );
			shareableIdRef.current = null;
			setExpirationDate( null );
			return;
		}

		let cancelled = false;

		// Drop the old code immediately: it describes the old selection, and the share
		// buttons would hand it out until the save lands.
		setShareableId( null );
		shareableIdRef.current = null;

		// Each save mints a server-side transient, so let a burst of clicks settle first.
		const timeoutId = setTimeout( () => {
			// Only include integer params when valid — omitting them avoids REST
			// type validation errors for null values.
			const requestBody = {
				products: selectedProducts || [],
				quantities: productQuantities,
			};
			if ( selectedBaseModel > 0 ) {
				requestBody.baseModel = selectedBaseModel;
			}
			if ( selectedBundle ) {
				requestBody.bundle = selectedBundle;
			}

			window.fetch( atConfigurator.apiUrl + 'configurations', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': atConfigurator.nonce,
				},
				body: JSON.stringify( requestBody ),
			} )
				.then( res => {
					if ( ! res.ok ) {
						throw new Error( 'Save failed' );
					}
					return res.json();
				} )
				.then( response => {
					if ( cancelled ) {
						return;
					}
					if ( response && response.code ) {
						setShareableId( response.code );
						shareableIdRef.current = response.code;
						if ( response.expires_at ) {
							setExpirationDate( response.expires_at );
						}
					}
				} )
				.catch( () => {
					// Configuration will still work, just won't be shareable.
				} );
		}, SAVE_DEBOUNCE_MS );

		return () => {
			cancelled = true;
			clearTimeout( timeoutId );
		};
	}, [ selectedBaseModel, selectedProducts, selectedBundle, productQuantities ] );

	// Drop quantities for products that are no longer selected, so a deselected item
	// can't carry its old count back in when it is re-added.
	useEffect( () => {
		setProductQuantities( previous => {
			const stale = Object.keys( previous ).filter(
				productId => ! selectedProducts.includes( parseInt( productId, 10 ) )
			);

			if ( stale.length === 0 ) {
				return previous;
			}

			const next = { ...previous };
			stale.forEach( productId => delete next[ productId ] );
			return next;
		} );
	}, [ selectedProducts ] );

	useEffect( () => {
		const urlParams = new URLSearchParams( window.location.search );
		const bundleId = urlParams.get( 'bundle' );

		// If bundle ID is in URL, BundleLoader component will handle it.
		// Don't load from code if bundle is in URL.
		if ( bundleId ) {
			// BundleLoader will handle this, just set the bundle ID.
			setSelectedBundle( parseInt( bundleId, 10 ) );
			isInitialLoad.current = false;
			return;
		}

		const codeParam = urlParams.get( 'code' );
		if ( codeParam ) {
			// Clean the code parameter (remove any invalid characters)
			const cleanCode = codeParam.replace( /[^a-zA-Z0-9-]/g, '' );

			if ( ! cleanCode ) {
				isInitialLoad.current = false;
				return;
			}

			apiFetch( {
				path: `/at-configurator/v1/configurations/${ cleanCode }`,
			} )
				.then( response => {
					if ( response && ! response.code ) {
						isInitialLoad.current = false;
						return;
					}

					if ( response ) {
						// Mark that we're loading from code to prevent save effect from running
						hasLoadedFromCode.current = true;

						// Set the loaded configuration - always set values even if null/empty
						// Ensure baseModel and bundle are integers if they exist
						const baseModelValue = response.baseModel ? parseInt( response.baseModel, 10 ) : null;
						const bundleValue = response.bundle ? parseInt( response.bundle, 10 ) : null;
						const productsValue = Array.isArray( response.products )
							? response.products.map( id => parseInt( id, 10 ) )
							: [];

						setSelectedBaseModel( baseModelValue );
						setSelectedProducts( productsValue );
						setSelectedBundle( bundleValue );
						setProductQuantities(
							response.quantities && typeof response.quantities === 'object' ? response.quantities : {}
						);
						setShareableId( response.code );
						shareableIdRef.current = response.code;
						if ( response.expires_at ) {
							setExpirationDate( response.expires_at );
						}

						// Mark initial load as complete after state updates
						isInitialLoad.current = false;
					} else {
						isInitialLoad.current = false;
					}
				} )
				.catch( () => {
					// Configuration not found or expired
					isInitialLoad.current = false;
				} );
		} else {
			// No code parameter, mark initial load as complete.
			isInitialLoad.current = false;
		}
		// Only run on mount - we don't want to re-fetch if URL changes after initial load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	/**
	 * Set how many of a product the configuration includes.
	 *
	 * A quantity of 1 is the implicit default, so it drops out of the map rather than
	 * being stored: that keeps the saved payload (and the shareable code) minimal.
	 *
	 * @param {number} productId Product ID.
	 * @param {number} quantity  Desired quantity.
	 */
	const setProductQuantity = useCallback( ( productId, quantity ) => {
		setProductQuantities( previous => {
			const next = { ...previous };
			const units = normaliseQuantity( quantity );

			if ( units > 1 ) {
				next[ productId ] = units;
			} else {
				delete next[ productId ];
			}

			return next;
		} );
	}, [] );

	/**
	 * Clear configuration
	 */
	const clearConfiguration = () => {
		setSelectedBaseModel( null );
		setSelectedProducts( [] );
		setSelectedBundle( null );
		setProductQuantities( {} );
		setShareableId( null );
		shareableIdRef.current = null;
		setExpirationDate( null );
	};

	/**
	 * Generate shareable URL with code parameter.
	 *
	 * Accepts the code as an explicit argument so callers use the reactive
	 * shareableId state rather than relying on the ref being in sync.
	 *
	 * @param {string} code Shareable code to embed in the URL.
	 * @return {string} URL with ?code= parameter.
	 */
	const getShareableUrl = useCallback( code => {
		const url = new URL( window.location.href );

		url.searchParams.delete( 'config' );
		url.searchParams.delete( 'bundle' );
		url.searchParams.delete( 'products' );
		url.searchParams.delete( 'baseModel' );
		url.searchParams.delete( 'code' );

		if ( code ) {
			url.searchParams.set( 'code', code );
		}

		return url.toString();
	}, [] );

	return {
		selectedBaseModel,
		setSelectedBaseModel,
		selectedProducts,
		setSelectedProducts,
		selectedBundle,
		setSelectedBundle,
		productQuantities,
		setProductQuantity,
		shareableId,
		expirationDate,
		clearConfiguration,
		getShareableUrl,
	};
};
